import { Link } from 'react-router-dom';
import { Mail, MessageCircle, MessageSquare } from 'lucide-react';
import { Card } from '@/components/ui';

export function ClientSupportPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-fg">Support</h1>
        <p className="mt-0.5 text-sm text-fg-muted">
          Need help with your Authmatech integration? We're here for you.
        </p>
      </div>

      <div className="rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 dark:border-primary-500/25 dark:bg-primary-500/10">
        <div className="flex items-start gap-3">
          <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
          <div>
            <p className="text-sm font-semibold text-primary-700 dark:text-primary-300">Submit a formal request</p>
            <p className="mt-0.5 text-xs text-primary-600/80 dark:text-primary-400">
              Need more credits, an expiry extension, or a package renewal?{' '}
              <Link to="/portal/requests" className="font-medium underline underline-offset-2">
                Go to My Requests
              </Link>{' '}
              to submit and track your request directly.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-primary-50 p-3 dark:bg-primary-500/15">
              <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-fg">Email Support</h2>
              <p className="mt-1 text-xs text-fg-muted">
                Send us an email and we'll get back to you within 24 hours on business days.
              </p>
              <a
                href="mailto:support@authmatech.com"
                className="mt-3 inline-flex items-center text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                support@authmatech.com
              </a>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-start gap-4">
            <div className="rounded-lg bg-emerald-50 p-3 dark:bg-emerald-500/15">
              <MessageCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-fg">Technical Documentation</h2>
              <p className="mt-1 text-xs text-fg-muted">
                Browse our API documentation for integration guides and reference.
              </p>
              <a
                href="https://service.authmatech.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center text-xs font-medium text-emerald-600 hover:underline dark:text-emerald-400"
              >
                service.authmatech.com ↗
              </a>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <h2 className="text-sm font-semibold text-fg">Common Questions</h2>
        <div className="mt-4 space-y-4">
          {FAQ.map(({ q, a }) => (
            <div key={q} className="border-b border-border pb-4 last:border-0 last:pb-0">
              <p className="text-sm font-medium text-fg">{q}</p>
              <p className="mt-1 text-xs text-fg-muted">{a}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const FAQ = [
  {
    q: 'How does silent mobile verification work?',
    a: "Authmatech silently verifies a user's mobile number using the mobile network operator — no OTP or SMS is sent. Your application sends the encrypted MSISDN payload and we confirm whether it matches the SIM on device.",
  },
  {
    q: 'What happens when my balance runs out?',
    a: 'Verification requests will be rejected once your credit balance reaches zero or your balance expiry date passes. Contact Authmatech to top up your balance.',
  },
  {
    q: 'Where can I find my API key?',
    a: 'Your API key is available from the Authmatech team when your account is activated. For security reasons, the raw API key is only shown once. If you need to rotate it, contact your account manager.',
  },
];
